import { useCallback, useEffect, useMemo, useState } from 'react';
import { listAgencyClinicPhysicians } from '../../services/api';
import type { ClinicPhysicianResponse } from '../../services/api/types.gen';
import { formatPhysicianName } from '../../utils/formatting';

export interface UseClinicPhysiciansResult {
  /** Active physicians in the clinic that have an NPI, sorted by name. */
  physicians: ClinicPhysicianResponse[];
  isLoading: boolean;
  /** Set when the roster could not be loaded; callers fall back to free text. */
  error: string | null;
  refetch: () => void;
}

/**
 * Physicians of a clinic, for pickers that store an NPI.
 *
 * Passing a null clinic id is a supported no-op: it resolves to an empty
 * roster without a request, so a form rendered outside a visit stays inert.
 */
export function useClinicPhysicians(
  agencyClinicId: string | null
): UseClinicPhysiciansResult {
  const [physicians, setPhysicians] = useState<ClinicPhysicianResponse[]>([]);
  // Starts true when a clinic is already known, so the first paint is the
  // loading dropdown rather than a frame of the free-text fallback.
  const [isLoading, setIsLoading] = useState(Boolean(agencyClinicId));
  const [error, setError] = useState<string | null>(null);
  const [reloadToken, setReloadToken] = useState(0);

  const refetch = useCallback(() => setReloadToken(token => token + 1), []);

  useEffect(() => {
    if (!agencyClinicId) {
      setPhysicians([]);
      setIsLoading(false);
      setError(null);
      return;
    }

    let cancelled = false;
    setIsLoading(true);
    // Drop a previous failure up front. Holding it across a retry would send
    // the field back to its free-text fallback for the length of the request,
    // which reads as the picker breaking right after a successful add.
    setError(null);

    listAgencyClinicPhysicians({
      path: { agency_clinic_id: agencyClinicId },
    })
      .then(response => {
        if (cancelled) return;
        if (response.error || !response.data) {
          console.error('Clinic physician load failed', response.error);
          setError('Could not load physicians for this clinic.');
        } else {
          setPhysicians(response.data);
          setError(null);
        }
        // Settled in the same tick as the data so the field does not render
        // an extra frame between "loaded" and "no longer loading".
        setIsLoading(false);
      })
      .catch(err => {
        if (cancelled) return;
        console.error('Clinic physician load failed', err);
        setError('Could not load physicians for this clinic.');
        setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [agencyClinicId, reloadToken]);

  // A physician with no NPI cannot back an NPI answer, so it is not offered.
  const withNpi = useMemo(
    () =>
      physicians
        .filter(physician => Boolean(physician.npi))
        .sort((a, b) =>
          formatPhysicianName(a).localeCompare(formatPhysicianName(b))
        ),
    [physicians]
  );

  return { physicians: withNpi, isLoading, error, refetch };
}
